import APP_URL_CONSTS from "../navigation/AppUrls";

type page = {
  id: string;
  pageTitle: string;
  showLogo: boolean;
  showHelpButton: boolean;
};

type pageRule = {
  id: string;
  matches: (url: string, productType?: string) => boolean;
  pageTitle: string;
  showLogo?: boolean;
};

const helpUrlList: Array<{ url: string; excludedProducts?: string[] }> = [
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.WORK_DETAILS}` },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.ELIGIBILITY_DETAILS}` },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.ABSENCE_DETAILS}` },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.PAYMENT_DETAILS}` },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.VOC_REHB_SUPPORT}` },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.CLAIM_DETAILS}` },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.DECEASED_DETAILS}` },
  { url: `${APP_URL_CONSTS.CLAIMS}` },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.TRACKING}` },
  {
    url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.EMPLOYEE_DETAILS}`,
    excludedProducts: ["GIP", "GCI"],
  },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.OTHER_DETAILS}` },
  { url: `${APP_URL_CONSTS.CLAIMS}/${APP_URL_CONSTS.CLAIM_SUMMARY}` },
];

// this do because of the dashboard board just being /claims, this would always return true if include was used,
// so we need to check the slug specifically. Include can then be used for the product related URLs.
export const checkBasedOnSlug = (expectedSlug: string, url: string) => {
  const slug = url.split("/").pop()?.replace(/\//g, "") ?? "";
  return slug === expectedSlug.replace(/\//g, "");
};

export const urlIncludesSegmentWithProductType = (
  expectedURL: string,
  expectedProductType: string,
  url: string,
  productType = ""
): boolean => url.includes(expectedURL) && productType === expectedProductType;

export const getShowHelp = (url: string, product = "") => {
  return (
    helpUrlList.some(
      (cfg) =>
        cfg.url === url && !(cfg.excludedProducts || []).includes(product)
    ) ?? false
  );
};

const MAKE_A_CLAIM = "Make a claim";
const CLAIMS_TITLE = "Claims";
const VIEW_CLAIM_TITLE = "View claim";
const POLICY_SELECTION_TITLE = "Policy selection";

const pageRules: pageRule[] = [
  {
    id: "dashboard",
    matches: (url) => checkBasedOnSlug(APP_URL_CONSTS.CLAIMS, url),
    pageTitle: CLAIMS_TITLE,
  },
  {
    id: "tracking",
    matches: (url) => checkBasedOnSlug(APP_URL_CONSTS.TRACKING, url),
    pageTitle: VIEW_CLAIM_TITLE,
  },
  {
    id: "policy-selection",
    matches: (url) => checkBasedOnSlug(APP_URL_CONSTS.POLICY_SELECTION, url),
    pageTitle: POLICY_SELECTION_TITLE,
  },
  {
    id: "GIP-claims-path",
    matches: (url: string, productType?: string) =>
      urlIncludesSegmentWithProductType(
        APP_URL_CONSTS.CLAIMS,
        "GIP",
        url,
        productType
      ),
    pageTitle: `${MAKE_A_CLAIM} (Group Income Protection)`,
  },
  {
    id: "GCI-claims-path",
    matches: (url: string, productType?: string) =>
      urlIncludesSegmentWithProductType(
        APP_URL_CONSTS.CLAIMS,
        "GCI",
        url,
        productType
      ),
    pageTitle: `${MAKE_A_CLAIM} (Group Critical Illness)`,
  },
  {
    id: "GIPVRS-claims-path",
    matches: (url: string, productType?: string) =>
      urlIncludesSegmentWithProductType(
        APP_URL_CONSTS.CLAIMS,
        "GIPVRS",
        url,
        productType
      ),
    pageTitle: `Request Absence management support`,
  },
  {
    id: "GLI-claims-path",
    matches: (url: string, productType?: string) =>
      urlIncludesSegmentWithProductType(
        APP_URL_CONSTS.CLAIMS,
        "GLI",
        url,
        productType
      ),
    pageTitle: `${MAKE_A_CLAIM} (Group Life insurance)`,
  },
];

export const getPage = (url: string, product = ""): page => {
  let pageRule: pageRule | undefined = pageRules.find((rule) =>
    rule.matches(url, product)
  );

  if (!pageRule) {
    return {
      id: "default",
      pageTitle: "",
      showLogo: true,
      showHelpButton: false,
    };
  }

  return {
    id: pageRule.id,
    pageTitle: pageRule.pageTitle,
    showLogo: pageRule.showLogo ?? false,
    showHelpButton: getShowHelp(url, product),
  };
};
